/**
 * Flabom — PDP (Figma "02 • PDP Donatella", desktop 51:3 / mobile 75:12).
 *
 * Cobre as 5 seções da frente J:
 *   flabom-pdp-hero · flabom-pdp-story · flabom-pdp-specifications ·
 *   flabom-pdp-related · flabom-pdp-reviews
 *
 * Mecânicas reaproveitadas do Horizon (sem editar os arquivos originais):
 *   - `<product-form-component on:submit="/handleSubmit">` (assets/product-form.js)
 *     faz o add to cart e dispara `CartAddEvent`, que abre o cart drawer.
 *   - `<product-recommendations>` (assets/product-recommendations.js) carrega os
 *     cards relacionados pela Section Rendering API.
 *   - `<flabom-shipping-calculator>` (snippets/flabom-shipping-calculator.liquid)
 *     calcula o frete por CEP; aqui só abrimos o painel (o Figma mostra expandido).
 *
 * A troca de variante é própria: o variant-picker do Horizon remonta a seção inteira
 * via morph e depende dos blocos dele (price/buy-buttons/media-gallery), que não
 * existem neste desenho. Aqui a troca é local e atualiza preço, parcelas, badge,
 * imagem, rótulo de cor, input `name="id"` do form e a URL.
 */

const WISHLIST_KEY = 'flabom:wishlist';

/* ----------------------------- money helpers ----------------------------- */

/**
 * @param {number} cents
 * @param {number} precision
 * @param {string} thousands
 * @param {string} decimal
 * @returns {string}
 */
function withDelimiters(cents, precision, thousands, decimal) {
  const amount = (Number(cents) / 100).toFixed(precision);
  const [integer, fraction] = amount.split('.');
  const grouped = integer.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
}

/**
 * Reimplementa o filtro `money` do Liquid a partir de `shop.money_format`.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const template = format || 'R$ {{amount_with_comma_separator}}';
  const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
  const key = match ? match[1] : 'amount';
  let value;

  switch (key) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
    case 'amount_with_comma_separated':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
      break;
  }

  return template.replace(/\{\{\s*\w+\s*\}\}/, value);
}

/* -------------------------------- wishlist -------------------------------- */

/** @returns {string[]} */
function readWishlist() {
  try {
    const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
}

/** @param {string[]} list */
function writeWishlist(list) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch {
    /* sessão sem storage: o coração ainda alterna, só não persiste */
  }
}

function bindWishlist(root) {
  const buttons = root.querySelectorAll('[data-fb-wish]');
  if (!buttons.length) return;

  const list = readWishlist();

  buttons.forEach((button) => {
    const id = button.dataset.productId;
    if (!id) return;

    if (list.includes(id)) {
      button.setAttribute('aria-pressed', 'true');
      button.textContent = '♥';
    }

    button.addEventListener('click', () => {
      const current = readWishlist();
      const index = current.indexOf(id);
      const next = index === -1;

      if (next) current.push(id);
      else current.splice(index, 1);

      writeWishlist(current);
      button.setAttribute('aria-pressed', next ? 'true' : 'false');
      button.textContent = next ? '♥' : '♡';
    });
  });
}

/* --------------------------------- hero ---------------------------------- */

class FlabomPdpHero {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '';
    this.installments = Number(root.dataset.installments) || 1;
    this.pixPercent = Number(root.dataset.pixPercent) || 0;
    this.anchorPct = Number(root.dataset.fbAnchorPct) || 0;
    this.anchorEnabled = root.dataset.fbAnchorEnabled !== 'false';

    /** @type {Array<{id:number,available:boolean,title:string,options:string[],price:number,compare_at_price:number,media_id:number,media_key:string,media_src:string}>} */
    this.variants = [];
    try {
      this.variants = JSON.parse(root.querySelector('[data-fb-pdp-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.mainImages = Array.from(root.querySelectorAll('[data-fb-main]'));
    this.thumbs = Array.from(root.querySelectorAll('[data-fb-thumb]'));
    this.thumbItems = Array.from(root.querySelectorAll('[data-fb-thumb-item]'));
    this.thumbMore = root.querySelector('[data-fb-thumb-more]')?.closest('li') || null;
    this.thumbMoreCount = root.querySelector('[data-fb-thumb-more-count]');
    this.counter = root.querySelector('[data-fb-counter]');
    this.counterTotal = root.querySelector('[data-fb-counter-total]');
    this.toast = root.querySelector('[data-fb-toast]');
    this.variantInput = root.querySelector('[data-fb-variant-id]');
    this.index = 0;

    const current = this.variants.find((variant) => String(variant.id) === this.variantInput?.value);
    /** @type {string[]} */
    this.selection = current ? current.options.slice() : this.variants[0]?.options.slice() || [];

    this.#bindGallery();
    this.#bindOptions();
    this.#bindQuantity();
    this.#bindShare();
    this.#bindTitleShift();
    this.#bindStickyBar();
    bindWishlist(root);

    // O Liquid já renderiza a galeria filtrada pela variante selecionada (sem
    // flash); aqui só reconciliamos o estado — e cobrimos o caso de a variante vir
    // da URL depois do render.
    this.#filterMedia(current || this.variants[0] || null);
    this.show(this.#visible()[0] ?? 0);
  }

  /**
   * Índices (na lista COMPLETA de mídias) que estão visíveis para a cor atual.
   * @returns {number[]}
   */
  #visible() {
    const own = [];
    const generic = [];
    this.mainImages.forEach((image, index) => {
      if (!image.hidden) (image.dataset.mediaKey ? own : generic).push(index);
    });
    // Fotos da cor primeiro: genérica (ex. as 2 vermelhas da Olympia) não abre a galeria.
    return own.length ? own.concat(generic) : generic;
  }

  /**
   * Galeria por cor: some com toda mídia que pertence a OUTRA cor (prefixo do nome
   * do arquivo), renumera a fila visível, mostra as 5 primeiras miniaturas e
   * recalcula o "+N" a partir do que sobrou.
   * @param {{media_key?:string}|null} variant
   */
  #filterMedia(variant) {
    const key = variant?.media_key || '';

    this.mainImages.forEach((image) => {
      const mediaKey = image.dataset.mediaKey || '';
      image.hidden = Boolean(mediaKey) && Boolean(key) && mediaKey !== key;
    });

    const queue = this.#visible();
    this.thumbItems.forEach((item, index) => {
      const mediaKey = item.dataset.mediaKey || '';
      if (mediaKey && key && mediaKey !== key) {
        item.hidden = true;
        delete item.dataset.vis;
        return;
      }
      const rank = queue.indexOf(index);
      item.style.order = String(rank);
      if (rank < 5) {
        item.hidden = false;
        item.dataset.vis = String(rank);
      } else {
        item.hidden = true;
        delete item.dataset.vis;
      }
    });

    const extra = queue.length - 5;
    if (this.thumbMore) this.thumbMore.hidden = extra <= 0;
    if (this.thumbMoreCount) this.thumbMoreCount.textContent = String(extra);
    if (this.counterTotal) this.counterTotal.textContent = String(queue.length);
  }

  /* -------------------------------- galeria -------------------------------- */

  #bindGallery() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.fbThumb)));
    });

    this.root.querySelector('[data-fb-thumb-more]')?.addEventListener('click', () => this.step(1));

    this.root.querySelector('[data-fb-prev]')?.addEventListener('click', () => this.step(-1));
    this.root.querySelector('[data-fb-next]')?.addEventListener('click', () => this.step(1));

    const dialog = this.root.querySelector('[data-fb-zoom-dialog]');
    const zoomImage = this.root.querySelector('[data-fb-zoom-image]');

    this.root.querySelector('[data-fb-zoom]')?.addEventListener('click', () => {
      const active = this.mainImages[this.index];
      if (!dialog || !zoomImage || !active) return;
      zoomImage.src = active.currentSrc || active.src;
      zoomImage.alt = active.alt;
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });

    this.root.querySelector('[data-fb-zoom-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  /**
   * @param {number} index Índice na lista COMPLETA de mídias.
   */
  show(index) {
    const visible = this.#visible();
    if (!visible.length) return;

    const found = visible.indexOf(index);
    const rank = found === -1 ? 0 : found;
    this.index = visible[rank];

    this.mainImages.forEach((image, i) => image.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === this.index));
    if (this.counter) this.counter.textContent = String(rank + 1);
    if (this.counterTotal) this.counterTotal.textContent = String(visible.length);
  }

  /**
   * Anda na fila VISÍVEL (as setas não podem cair numa mídia de outra cor).
   * @param {number} delta
   */
  step(delta) {
    const visible = this.#visible();
    if (!visible.length) return;
    const current = visible.indexOf(this.index);
    const rank = (((current === -1 ? 0 : current) + delta) % visible.length + visible.length) % visible.length;
    this.show(visible[rank]);
  }

  /** @param {number} mediaId */
  showMedia(mediaId) {
    const visible = this.#visible();
    if (!visible.length) return;
    const index = this.mainImages.findIndex((image) => Number(image.dataset.mediaId) === Number(mediaId));
    this.show(index !== -1 && !this.mainImages[index].hidden ? index : visible[0]);
  }

  /* ------------------------------- variantes ------------------------------- */

  #bindOptions() {
    const groups = [
      { list: this.root.querySelector('[data-fb-swatches]'), attribute: 'fbSwatch' },
      { list: this.root.querySelector('[data-fb-sizes]'), attribute: 'fbSize' },
    ];

    groups.forEach(({ list, attribute }) => {
      if (!list) return;
      const position = Number(list.dataset.optionPosition) || 1;

      list.querySelectorAll(`[data-${attribute === 'fbSwatch' ? 'fb-swatch' : 'fb-size'}]`).forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset[attribute];
          if (value == null) return;

          this.selection[position - 1] = value;

          list.querySelectorAll('button').forEach((sibling) => {
            const isSelected = sibling === button;
            sibling.classList.toggle('is-selected', isSelected);
            sibling.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });

          this.apply();
        });
      });
    });
  }

  apply() {
    const variant =
      this.variants.find((candidate) => candidate.options.every((value, i) => value === this.selection[i])) || null;

    if (!variant) return;

    if (this.variantInput) this.variantInput.value = String(variant.id);

    const price = variant.price;
    const compare = variant.compare_at_price || 0;
    const installmentValue = Math.floor(price / this.installments); // D10: nunca acima do preço
    const pix = Math.floor((price * (100 - this.pixPercent)) / 100);

    // Mesma regra do Liquid: o "de" é o compare_at quando ele é REAL; senão é
    // derivado de trás para frente — preço ÷ (1 − pct/100) — para que o preço
    // atual fique exatamente `anchorPct`% abaixo do "de" (0 = nunca fabricar).
    let anchor = 0;
    if (compare > price) anchor = compare;
    else if (this.anchorEnabled && this.anchorPct > 0) anchor = Math.round((price * 100) / (100 - this.anchorPct));

    this.#text('[data-fb-price]', this.#money(price));
    this.#text('[data-fb-cash]', this.#money(price));
    this.#text('[data-fb-pix]', this.#money(pix));
    this.#text('[data-fb-pix-pct]', String(this.pixPercent));
    this.#text('[data-fb-compare]', this.#money(anchor));
    this.#text('[data-fb-installments-count]', String(this.installments));
    this.#text('[data-fb-installment-value]', this.#money(installmentValue));

    this.root.querySelector('[data-fb-anchor]')?.classList.toggle('is-hidden', anchor <= price);

    const colorName = this.selection[0] || variant.title;
    this.#text('[data-fb-color-name]', colorName);
    this.#text('[data-fb-color-name-m]', colorName.toUpperCase());

    const button = this.root.querySelector('[data-fb-atc]');
    if (button) button.disabled = !variant.available;
    const stickyButton = this.root.querySelector('[data-fb-sticky-atc]');
    if (stickyButton) stickyButton.disabled = !variant.available;
    this.#text('[data-fb-sticky-price]', this.#money(price));

    this.#filterMedia(variant);
    this.showMedia(variant.media_id);
    this.#syncSpecImage(variant);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    } catch {
      /* URL inválida (preview do editor): a troca visual continua valendo */
    }
  }

  /**
   * A imagem da esquerda da seção de especificações é a FOTO da cor selecionada
   * (quando não há imagem de ficha técnica no metafield/na seção).
   * @param {{media_src?:string}} variant
   */
  #syncSpecImage(variant) {
    const src = variant?.media_src;
    if (!src) return;
    document.querySelectorAll('[data-fb-spec-image]').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      image.srcset = '';
      image.src = src;
    });
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #text(selector, value) {
    const element = this.root.querySelector(selector);
    if (element) element.textContent = value;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.moneyFormat);
  }

  /* ------------------------------- quantidade ------------------------------ */

  #bindQuantity() {
    const input = this.root.querySelector('[data-fb-qty-input]');
    if (!input) return;

    this.root.querySelectorAll('[data-fb-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.fbQty) || 0;
        const next = Math.max(1, (Number(input.value) || 1) + step);
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    input.addEventListener('change', () => {
      const value = Math.max(1, Math.floor(Number(input.value) || 1));
      input.value = String(value);
    });
  }

  /* ------------------------------ compartilhar ----------------------------- */

  #bindShare() {
    const button = this.root.querySelector('[data-fb-share]');
    if (!button) return;

    let timer = 0;
    const flash = () => {
      if (!this.toast) return;
      this.toast.hidden = false;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (this.toast) this.toast.hidden = true;
      }, 2000);
    };

    button.addEventListener('click', async () => {
      const url = button.dataset.shareUrl || window.location.href;
      const title = button.dataset.shareTitle || document.title;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch {
          /* usuário cancelou o share nativo: cai no copiar link */
        }
      }

      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard API bloqueada (contexto inseguro / permissão negada): copia por
        // um campo temporário. Nunca abrir window.prompt — diálogo do browser na
        // PDP é ruído, e o aviso aqui é o toast "Link copiado".
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.append(field);
        field.select();
        try {
          document.execCommand('copy');
        } catch {
          /* sem cópia possível: o toast ainda confirma a ação do botão */
        }
        field.remove();
      }

      button.setAttribute('data-copied', 'true');
      setTimeout(() => button.removeAttribute('data-copied'), 1600);
      flash();
    });
  }

  /* ------------------------------ título mobile ----------------------------- */

  /**
   * Auditoria D3: no mobile o título tem até 3 linhas. O painel mobile é absoluto
   * (coordenadas do Figma), então tudo abaixo do título desce o que ele cresceu
   * além de 1 linha — via --fb-title-shift (o Liquid já manda uma estimativa).
   */
  #bindTitleShift() {
    const title = this.root.querySelector('.fb-pdp-hero__title');
    if (!title || !('ResizeObserver' in window)) return;

    const mobile = window.matchMedia('(max-width: 1023px)');
    const update = () => {
      if (!mobile.matches) return;
      const line = parseFloat(window.getComputedStyle(title).lineHeight) || 36;
      this.root.style.setProperty('--fb-title-shift', `${Math.max(0, title.offsetHeight - line)}px`);
    };

    new ResizeObserver(update).observe(title);
    mobile.addEventListener('change', update);
  }

  /* --------------------------- barra fixa de compra ------------------------- */

  /**
   * Auditoria D8: no mobile, quando o botão principal sai da tela, aparece uma
   * barra fixa com preço + "Adicionar à sacola". O botão da barra só clica no
   * botão real do formulário (mesmo fluxo de add to cart do Horizon).
   */
  #bindStickyBar() {
    const bar = this.root.querySelector('[data-fb-sticky]');
    const atc = this.root.querySelector('[data-fb-atc]');
    if (!bar || !atc || !('IntersectionObserver' in window)) return;

    const button = bar.querySelector('[data-fb-sticky-atc]');
    button?.addEventListener('click', () => atc.click());

    new IntersectionObserver(([entry]) => {
      // Só depois de o botão ter passado (acima da tela), não antes de chegar nele.
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.hidden = !show;
    }).observe(atc);
  }
}

/* ------------------------------- relacionados ----------------------------- */

/** @param {HTMLElement} root */
function bindRelated(root) {
  const track = root.querySelector('[data-fb-related-track]');
  const dots = root.querySelector('[data-fb-related-dots]');
  bindWishlist(root);

  if (!track || !dots) return;

  const cards = Array.from(track.children);
  if (!cards.length) return;

  dots.textContent = '';
  cards.forEach((_, index) => {
    const dot = document.createElement('li');
    if (index === 0) dot.classList.add('is-active');
    dots.append(dot);
  });

  const items = Array.from(dots.children);

  track.addEventListener(
    'scroll',
    () => {
      const first = cards[0];
      if (!first) return;
      const step = first.getBoundingClientRect().width + 16;
      const index = Math.min(items.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
      items.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    },
    { passive: true }
  );
}

/* --------------------------------- reviews -------------------------------- */

/** @param {HTMLElement} root */
function bindReviews(root) {
  const card = root.querySelector('[data-fb-reviews-card]');
  const widget = root.querySelector('[data-fb-reviews-widget]');

  if (card && widget) {
    const sync = () => {
      card.dataset.hasWidget = widget.childElementCount > 0 ? 'true' : 'false';
      // Sem avaliação: marca as partes do placar zerado para o CSS esconder (revisão 08/10).
      const main = widget.querySelectorAll(':scope > .bk-reviews')[1];
      const zero = !!main && /Seja o primeiro/.test(main.textContent || '');
      card.dataset.zero = zero ? 'true' : 'false';
      if (zero) {
        const select = main.querySelector('select');
        const actions = select?.parentElement?.parentElement;
        if (actions) {
          select.parentElement.setAttribute('data-fb-zero-hide', '');
          actions.previousElementSibling?.setAttribute('data-fb-zero-hide', '');
          actions.setAttribute('data-fb-zero-center', '');
          actions.parentElement?.setAttribute('data-fb-zero-center', '');
        }
        main.querySelector('.bk-theme-bg-color > div')?.setAttribute('data-fb-zero-pad', '');
        // texto "Seja o primeiro…" antes do botão
        actions?.parentElement?.parentElement?.setAttribute('data-fb-zero-col', '');
      }
      // Botão "Escrever uma avaliação" do BK no navy da marca (era cinza-ardósia).
      widget.querySelectorAll('button').forEach((b) => {
        if (/Escrever uma avalia/.test(b.textContent || '')) b.setAttribute('data-fb-bk-write', '');
      });
    };

    // O app BK Reviews (App Embed global) auto-injeta `div.bk-reviews` no primeiro
    // container de produto que acha na página — sem hook de PDP no tema, ele caía
    // dentro da seção de relacionados e esticava a página. Trazemos o widget para o
    // hook do card de avaliações, que é onde o Figma o colocou.
    const adopt = () => {
      document.querySelectorAll('.bk-reviews').forEach((node) => {
        if (node === widget || widget.contains(node)) return;
        widget.append(node);
      });
      sync();
    };

    adopt();
    new MutationObserver(adopt).observe(document.body, { childList: true, subtree: true });
  }

  // "Escrever avaliação": o widget do BK Reviews injeta o próprio botão dentro do
  // container. Se ele já existir, delegamos o clique; senão, mandamos o comprador
  // para o painel de perguntas (única via de contato garantida no tema).
  root.querySelectorAll('[data-fb-write-review]').forEach((button) => {
    button.addEventListener('click', () => {
      const appButton = widget?.querySelector('button, a');
      if (appButton instanceof HTMLElement) {
        appButton.click();
        return;
      }
      const field = root.querySelector('[data-fb-question-body]');
      if (field instanceof HTMLElement) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
      }
    });
  });

  bindQuestionForm(root);
}

/**
 * O Figma desenhou UM campo. O form de contato do Shopify exige e-mail, então o
 * envio tem 2 passos dentro da MESMA caixa: pergunta → e-mail → enviar.
 * @param {HTMLElement} root
 */
function bindQuestionForm(root) {
  const form = root.querySelector('[data-fb-question-form]');
  if (!(form instanceof HTMLFormElement)) return;

  const body = form.querySelector('[data-fb-question-body]');
  const email = form.querySelector('[data-fb-question-email]');
  const label = form.querySelector('[data-fb-question-label]');
  if (!body || !email || !label) return;

  const askText = label.textContent?.trim() || 'PERGUNTAR';
  const sendText = root.querySelector('[data-fb-reviews-card]')?.dataset.sendText || 'ENVIAR';
  let step = email.value ? 2 : 1;

  form.addEventListener('submit', (event) => {
    if (!body.value.trim()) {
      event.preventDefault();
      body.focus();
      return;
    }

    if (step === 1 && !email.value.trim()) {
      event.preventDefault();
      step = 2;
      email.hidden = false;
      email.required = true;
      label.textContent = sendText;
      email.focus();
      return;
    }

    if (step === 2 && !email.checkValidity()) {
      event.preventDefault();
      email.focus();
    }
  });

  // Voltar ao passo 1 se o comprador limpar o e-mail.
  email.addEventListener('input', () => {
    if (email.value.trim()) return;
    label.textContent = askText;
  });
}

/* ---------------------------------- boot ---------------------------------- */

function boot() {
  document.querySelectorAll('[data-fb-pdp-hero]').forEach((root) => {
    if (root.dataset.fbReady === 'true') return;
    root.dataset.fbReady = 'true';
    new FlabomPdpHero(root);
  });

  document.querySelectorAll('.fb-pdp-related').forEach((root) => {
    if (root.dataset.fbReady === 'true') return;
    root.dataset.fbReady = 'true';
    bindRelated(root);
  });

  document.querySelectorAll('.fb-pdp-reviews').forEach((root) => {
    if (root.dataset.fbReady === 'true') return;
    root.dataset.fbReady = 'true';
    bindReviews(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// Os cards relacionados chegam depois (Section Rendering API) e o tema remonta
// seções no editor: religa os bindings quando o DOM mudar.
new MutationObserver(() => {
  document.querySelectorAll('.fb-pdp-related[data-fb-ready="true"]').forEach((root) => {
    const track = root.querySelector('[data-fb-related-track]');
    if (track && track.dataset.fbBound !== 'true') {
      track.dataset.fbBound = 'true';
      bindRelated(root);
    }
  });
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', boot);
